import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { RoutesApp } from '../models/enums/routes-app.enum';
import { AuthService } from '../services/auth.service';

/** Evita múltiplos navigate quando várias requisições retornam 401 em paralelo. */
let unauthorizedRedirectInFlight = false;

/** Respostas 401 nestes paths da API não disparam logout (fluxo público). */
const PUBLIC_AUTH_PATHS = new Set([
  'auth/login',
  'auth/register/student',
  'auth/register/trainer',
  'auth/social',
  'auth/forgot-password',
  'auth/reset-password',
]);

function apiPathAfterBase(requestUrl: string, apiBase: string): string | null {
  if (!requestUrl.startsWith(apiBase)) {
    return null;
  }
  let tail = requestUrl.slice(apiBase.length);
  tail = tail.replace(/^\//, '');
  return tail.split('?')[0].split('#')[0] || '';
}

function shouldIgnore401ForRedirect(requestUrl: string): boolean {
  const path = apiPathAfterBase(requestUrl, environment.apiUrl);
  if (path === null) {
    return true;
  }
  return PUBLIC_AUTH_PATHS.has(path);
}

export const unauthorizedInterceptor: HttpInterceptorFn = (req, next) => {
  const injector = inject(Injector);
  const router = inject(Router);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status !== 401 || shouldIgnore401ForRedirect(req.url)) {
        return throwError(() => error);
      }

      if (unauthorizedRedirectInFlight) {
        return throwError(() => error);
      }
      unauthorizedRedirectInFlight = true;

      const auth = injector.get(AuthService);
      const userType = auth.getCurrentUser()?.userType;
      const queryParams =
        userType === 'trainer' ? { userType: 'trainer' as const } : { userType: 'aluno' as const };

      auth.clearSession();

      void router
        .navigate(['/' + RoutesApp.UNIFIED_LOGIN], { queryParams, replaceUrl: true })
        .finally(() => {
          unauthorizedRedirectInFlight = false;
        });

      return throwError(() => error);
    })
  );
};
